"use client";

import { useState } from "react";
import { Minus, Plus, TriangleAlert } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const tocItems = [{
  id: "m-01",
  index: "01",
  label: "MATERIAL SPECS"
}, {
  id: "m-02",
  index: "02",
  label: "ENGRAVING DEPTH"
}, {
  id: "m-03",
  index: "03",
  label: "MOUNTING GUIDES"
}, {
  id: "m-04",
  index: "04",
  label: "DPM STANDARDS"
}, {
  id: "m-05",
  index: "05",
  label: "FILE PREP"
}, {
  id: "m-06",
  index: "06",
  label: "QUALITY ASSURANCE"
}];
const manualEntries = [{
  id: "m-01",
  index: "M-01",
  title: "MATERIAL SPECS",
  instructions: ["Select industrial-grade phenolic or anodized aluminum for maximum durability.", "Verify material thickness requirements for your specific electrical enclosure.", "Ensure UV-stable materials are used for all outdoor-rated signage.", "Check chemical resistance ratings for harsh industrial environments.", "Consult our engineering team for custom material compatibility testing."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/material_spec_chart.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/material_spec_chart.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/material_spec_chart.png-wvc-sizes",
    alt: "Chart showing material durability ratings",
    tag: "[FIG. 01 / MATERIAL DATA]"
  },
  caution: "Always verify material thickness before finalizing your order specifications."
}, {
  id: "m-02",
  index: "M-02",
  title: "ENGRAVING DEPTH",
  instructions: ["Standard engraving depth is set to 0.020 inches for optimal readability.", "Deep engraving is available for high-wear environments or paint-fill applications.", "Laser marking provides high-contrast results on stainless steel surfaces.", "Depth consistency is maintained through automated laser calibration.", "Verify depth requirements against your project's specific safety standards."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_depth_diagram.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_depth_diagram.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/engraving_depth_diagram.png-wvc-sizes",
    alt: "Diagram showing laser engraving depth profiles",
    tag: "[FIG. 02 / DEPTH PROFILE]"
  },
  caution: "Excessive depth may compromise the structural integrity of thin substrates."
}, {
  id: "m-03",
  index: "M-03",
  title: "MOUNTING GUIDES",
  instructions: ["Use industrial-strength adhesive backing for smooth, clean surfaces.", "Mechanical fasteners are recommended for high-vibration or outdoor installations.", "Pre-drilled holes are available upon request for all custom nameplates.", "Ensure surfaces are degreased and dry before applying adhesive labels.", "Follow torque specifications when installing metal tags with hardware."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mounting_hardware_guide.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mounting_hardware_guide.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/mounting_hardware_guide.png-wvc-sizes",
    alt: "Diagram of recommended mounting hardware",
    tag: "[FIG. 03 / INSTALLATION]"
  },
  caution: "Improper surface preparation is the leading cause of adhesive failure."
}, {
  id: "m-04",
  index: "M-04",
  title: "DPM STANDARDS",
  instructions: ["Direct Part Marking (DPM) ensures permanent traceability for critical components.", "Laser annealing provides a non-destructive mark on stainless steel parts.", "Dot peen marking is ideal for deep, permanent identification on rough surfaces.", "Verify barcode readability against ISO/IEC 15415 standards.", "Maintain consistent marking parameters for batch-to-batch uniformity."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_marking_process.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_marking_process.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/dpm_marking_process.webp-wvc-sizes",
    alt: "Diagram of DPM laser marking process",
    tag: "[FIG. 04 / TRACEABILITY]"
  },
  caution: "Ensure part surface is free of oils and coatings before DPM processing."
}, {
  id: "m-05",
  index: "M-05",
  title: "FILE PREP",
  instructions: ["Submit vector files in .DXF, .AI, or .EPS formats for best results.", "Convert all text to outlines to prevent font substitution errors.", "Ensure all cut lines and engraving paths are clearly defined in layers.", "Include a reference dimension to verify scale accuracy for custom shapes.", "High-resolution raster images may require additional vectorization time."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/file_prep_workflow.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/file_prep_workflow.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/file_prep_workflow.webp-wvc-sizes",
    alt: "Workflow diagram for vector file preparation",
    tag: "[FIG. 05 / FILE SPECS]"
  },
  caution: "Incomplete files may delay production timelines. Please double-check layers."
}, {
  id: "m-06",
  index: "M-06",
  title: "QUALITY ASSURANCE",
  instructions: ["Every order undergoes a multi-point inspection for accuracy and finish.", "We verify all text against your approved submittal packages.", "Material batches are tracked to ensure full material certification.", "Final packaging is designed to protect finishes during transit.", "Contact our support team immediately if any discrepancies are noted."],
  figure: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/qa_inspection_process.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/qa_inspection_process.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1703715/img/qa_inspection_process.png-wvc-sizes",
    alt: "Diagram of quality assurance inspection steps",
    tag: "[FIG. 06 / QA PROTOCOL]"
  },
  caution: "Report any shipping damage within 24 hours of receipt for replacement."
}];
export default function Faq() {
  const [activeId, setActiveId] = useState<string>("");
  const {
    ref: bannerRef,
    isVisible: bannerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const handleTocClick = (id: string) => {
    const el = document.getElementById(`accordion-${id}`);
    if (el) {
      el.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  return <section data-section-id="1770" id="faq" className="bg-background py-28 md:py-40 text-foreground">
      <div className="mx-auto max-w-[1360px] px-6 md:px-8 lg:px-16">
        <div ref={bannerRef} className={`transition-all duration-500 ease-out ${bannerVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
          <div className="flex items-center gap-3 mb-6">
            <span className="inline-block size-2.5 bg-primary" aria-hidden="true" />
            <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.18em] text-foreground">
              § 01 — Technical Manual
            </span>
          </div>

          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 md:gap-10">
            <h1 className="font-default font-black uppercase leading-[0.88] tracking-tight text-foreground" style={{
            fontSize: "clamp(2.5rem, 8.2vw, 7.5rem)"
          }}>
              The{" "}
              <em className="not-italic md:italic font-black" style={{
              color: "var(--primary)",
              fontStyle: "italic"
            }}>
                Industrial
              </em>{" "}
              Engraving Guide.
            </h1>
            <div className="shrink-0">
              <span className="inline-block font-mono text-xs font-semibold uppercase tracking-[0.12em] text-foreground border border-foreground px-3 py-1.5">
                [REV. 2025.03 / 24 PAGES]
              </span>
            </div>
          </div>
        </div>

        <div className="mt-12 mb-16 h-6 w-full" style={{
        backgroundImage: "repeating-linear-gradient(45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px), repeating-linear-gradient(-45deg, var(--foreground) 0, var(--foreground) 2px, transparent 2px, transparent 8px)",
        backgroundColor: "var(--background)",
        opacity: 0.85
      }} aria-hidden="true" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12">
          <aside className="lg:col-span-3">
            <div className="lg:hidden -mx-6 px-6 overflow-x-auto pb-4">
              <div className="flex gap-2 min-w-max">
                {tocItems.map((item, i) => <button key={item.id} data-index={i} onClick={() => handleTocClick(item.id)} className={`font-mono text-xs font-semibold uppercase tracking-[0.12em] px-3 py-2 border whitespace-nowrap transition-colors ${activeId === item.id ? "border-primary bg-primary text-primary-foreground" : "border-foreground/40 text-foreground hover:border-foreground"}`}>
                    {item.index} / {item.label}
                  </button>)}
              </div>
            </div>

            <div className="hidden lg:block sticky top-24">
              <div className="font-mono text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground mb-6 flex items-center gap-2">
                <span className="inline-block size-2 bg-primary" aria-hidden="true" />
                Contents
              </div>
              <ul className="space-y-1">
                {tocItems.map((item, i) => {
                const isActive = activeId === item.id;
                return <li data-index={i} key={item.id}>
                      <button onClick={() => handleTocClick(item.id)} className={`group w-full text-left flex items-baseline gap-3 py-3 pr-2 border-l-2 transition-all duration-200 ${isActive ? "border-primary pl-4" : "border-transparent pl-2 hover:border-foreground/40 hover:pl-3"}`}>
                        <span className="font-mono text-xs font-semibold tracking-[0.12em] text-muted-foreground shrink-0">
                          {item.index} /
                        </span>
                        <span className={`font-default font-bold uppercase text-sm tracking-tight transition-colors ${isActive ? "text-foreground" : "text-foreground/80 group-hover:text-foreground"}`}>
                          {item.label}
                        </span>
                      </button>
                    </li>;
              })}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-9">
            <Accordion type="single" collapsible className="w-full" onValueChange={val => setActiveId(val || "")}>
              {manualEntries.map((entry, i) => <AccordionItem key={entry.id} value={entry.id} id={`accordion-${entry.id}`} data-index={i} className="border-t border-b-0 border-foreground last:border-b last:border-foreground data-[state=open]:border-l-4 data-[state=open]:border-l-primary transition-all">
                  <AccordionTrigger className="hover:no-underline py-8 md:py-9 [&>svg]:hidden group">
                    <div className="grid grid-cols-[64px_1fr_40px] md:grid-cols-[96px_1fr_40px] gap-4 md:gap-6 items-center w-full">
                      <span className="font-mono text-xs md:text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        [{entry.index}]
                      </span>
                      <span className="font-default font-bold uppercase tracking-tight leading-none text-left text-foreground" style={{
                    fontSize: "clamp(1.25rem, 2.1vw, 2.125rem)"
                  }}>
                        {entry.title}
                      </span>
                      <span className="size-10 flex items-center justify-center bg-primary text-primary-foreground justify-self-end shrink-0">
                        <Plus className="size-5 group-data-[state=open]:hidden" strokeWidth={2.5} />
                        <Minus className="size-5 hidden group-data-[state=open]:block" strokeWidth={2.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-10 pt-2">
                    <div className="md:ml-24 bg-card p-6 md:p-8 border border-foreground/15">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div>
                          <div className="font-mono text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground mb-4 flex items-center gap-2">
                            <span className="inline-block size-1.5 bg-primary" aria-hidden="true" />
                            Procedure
                          </div>
                          <ol className="space-y-3 font-default text-base leading-relaxed text-foreground">
                            {entry.instructions.map((step, j) => <li key={j} data-index={j} className="grid grid-cols-[28px_1fr] gap-3">
                                <span className="font-mono text-xs font-semibold tracking-[0.1em] text-muted-foreground pt-1">
                                  {String(j + 1).padStart(2, "0")}
                                </span>
                                <span>{step}</span>
                              </li>)}
                          </ol>
                          {entry.caution && <div className="mt-6 pt-4 border-t border-foreground/15">
                              <p className="font-mono text-xs font-semibold uppercase tracking-[0.1em] flex items-start gap-2 text-foreground">
                                <TriangleAlert className="size-4 shrink-0 mt-0.5" style={{
                            color: "var(--primary)"
                          }} strokeWidth={2.5} />
                                <span>Caution — {entry.caution}</span>
                              </p>
                            </div>}
                        </div>

                        <div className="relative">
                          <div className="relative">
                            <span className="absolute top-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute top-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 left-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <span className="absolute bottom-2 right-2 size-1.5 bg-foreground z-10" aria-hidden="true" />
                            <img src={entry.figure.src} data-wvc-srcset={entry.figure.srcset} data-wvc-sizes={entry.figure.sizes} alt={entry.figure.alt} loading="lazy" decoding="async" className="w-full h-auto object-cover border border-foreground/30" />
                          </div>
                          <div className="mt-3 flex items-center justify-between">
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              {entry.figure.tag}
                            </span>
                            <span className="font-mono text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                              REF. {entry.index}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}